import React, { useState } from "react";
import CommonIcons from "@base/assets/icons";
import { ISpecialPackage } from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import CommonStyles from "@components/CommonStyles";
import TagSpecialPackageStatus from "@components/CommonStyles/TagSpecialPackageStatus";
import InsuranceBadge from "@components/CommonStyles/InsuranceBadge";
import { Divider } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ECurrency, ESpecialPackageStatus } from "@base/enums";
import CommonImages from "@base/assets/images";
import { baseRoutes } from "@base/routes/baseRoutes";
import { getAvatarLinkFromObjectName } from "@base/helpers/common";
import { isExtraFlagOn, parseExtraInfo } from "@base/helpers/extraInfo";
import { isInsuranceSeller } from "@base/modules/patientSpecialPackage/packageDetailRules";

interface IProps {
  specialPackage: ISpecialPackage;
}

export default function ServicePackageCard({ specialPackage }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isInfoOpen, setIsInfoOpen] = useState<boolean>(false);
  // What the package itself says about its content, each part under its own heading.
  const infoSections = [
    { label: t("booking.packageInfo.description"), text: specialPackage.description },
    { label: t("booking.packageInfo.details"), text: specialPackage.details },
  ].filter((section) => !!section.text?.trim());

  const extraInfo = parseExtraInfo(specialPackage.extra_info);
  // Insurance packages are fulfilled through a partner, so the selling site is not something the
  // patient should act on - the package config decides whether to name it at all.
  const isSiteInfoHidden = isExtraFlagOn(extraInfo.site_info_hidden);

  const expiredDate = specialPackage.expired_date ? dayjs(specialPackage.expired_date, "YYYY-MM-DD") : undefined;
  // `HH:mm:ss` on its own is not something dayjs parses, and the seconds are noise anyway.
  const expiredTime = /^\d{2}:\d{2}/.test(specialPackage.expired_time ?? "") ? specialPackage.expired_time?.slice(0, 5) : "";
  const expiredAt = expiredDate?.isValid() ? [expiredTime, expiredDate.format("DD/MM/YYYY")].filter(Boolean).join(" ") : "";

  return (
    <div className="w-full bg-common-background p-3 rounded-2xl">
      <div className="flex justify-between items-center">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="text-black text-base font-medium font-['Inter'] line-clamp-1">{specialPackage.patient_record?.patient_name}</span>
          <span className="text-common-subtext text-xs font-normal font-['Inter'] line-clamp-1">
            {[
              specialPackage.patient_record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
              specialPackage.patient_record?.patient_dob ? dayjs(specialPackage.patient_record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </div>

        {/* An insurer's package carries the verified mark in the top right corner. */}
        <InsuranceBadge specialPackage={specialPackage} />
      </div>

      <Divider className="my-4" />

      <div className="flex justify-between items-center max-sm:flex-col max-sm:justify-start max-sm:items-start max-sm:gap-2">
        <div className="flex items-start gap-4">
          <div className="w-fit max-md:hidden">
            <CommonStyles.ImageUI height={125} width={222} src={specialPackage.thumbnail} fallback={CommonImages.DrafPackage} />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-base !leading-6 font-bold font-['Inter'] !mb-0">
                {specialPackage.package_name}
              </CommonStyles.TypographyUI>

              {infoSections.length ? (
                <button
                  type="button"
                  aria-label={t("booking.packageInfo.open")}
                  title={t("booking.packageInfo.open")}
                  // As tall as one line of the name, so the icon sits on the first line's centre however long the name runs.
                  className="shrink-0 h-6 flex items-center text-common-primary text-base"
                  onClick={() => setIsInfoOpen(true)}
                >
                  <CommonIcons.InfoCircleOutlined />
                </button>
              ) : null}
            </div>

            {/* Insurance-paid packages do not show the price to the user. */}
            {!isInsuranceSeller(specialPackage) ? (
              <CommonStyles.TypographyUI use="paragraph" className="text-common-inprogress text-base font-medium font-['Inter'] !mb-0">
                {specialPackage.amount?.toLocaleString()}
                {ECurrency.VND}
              </CommonStyles.TypographyUI>
            ) : null}

            {!isSiteInfoHidden ? (
              <div className="flex items-center gap-1">
                <CommonStyles.ImageUI
                  width={21}
                  height={21}
                  // Only the clinic's mark beside its name; there is nothing to enlarge.
                  preview={false}
                  src={getAvatarLinkFromObjectName("thumbnail", specialPackage.site?.avatar ?? [])}
                  fallback={CommonImages.location}
                />
                <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-xs font-normal font-['Inter'] !mb-0 inline-block">
                  {specialPackage.site?.name}
                  {specialPackage.site_branch_name ? ", " + specialPackage.site_branch_name : ""}
                </CommonStyles.TypographyUI>
              </div>
            ) : null}

            {/* The status sits with the package's details, as on the package list. */}
            <TagSpecialPackageStatus status={specialPackage.status as ESpecialPackageStatus} />

            {/* Last, after everything that describes the package. */}
            {expiredAt ? (
              <CommonStyles.TypographyUI use="paragraph" className="text-black text-sm font-normal font-['Inter'] !mb-0">
                {t("user.servicePackage.expiredAt", { time: expiredAt })}
              </CommonStyles.TypographyUI>
            ) : null}

            {/* {specialPackage.start_date ? (
              <div className="flex items-center gap-2">
                <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
                  Validity:{" "}
                </CommonStyles.TypographyUI>
                <CommonStyles.TypographyUI use="paragraph" className="text-common-negative text-xs font-normal font-['Inter'] !mb-0">
                  {dayjs(specialPackage.start_date, "YYYY-MM-DD").format("DD/MM/YYYY")}
                </CommonStyles.TypographyUI>
              </div>
            ) : null} */}
          </div>
        </div>
      </div>

      {isInfoOpen ? (
        <CommonStyles.ModalConfirmationUI
          open
          toggle={() => setIsInfoOpen(false)}
          type="normal"
          hiddenAction
          width={640}
          title={<span className="block pr-6">{specialPackage.package_name}</span>}
          content={
            <div className="flex flex-col gap-4 max-h-[calc(100vh-240px)] overflow-y-auto">
              {infoSections.map((section) => (
                <div key={section.label} className="flex flex-col gap-1">
                  <span className="text-common-maintext text-sm font-semibold font-['Inter']">{section.label}</span>
                  <span className="text-common-subtext text-sm font-normal font-['Inter'] whitespace-pre-line">{section.text?.trim()}</span>
                </div>
              ))}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
